import { View, Text } from "react-native";
import { openURL } from "expo-linking";
import DatePickerInput from "../../../components/DatePickerInput";
import { Control, FieldValues, Path, useController } from "react-hook-form";
import moment, { Moment } from "moment";
import { useFeatureFlags } from "../../../hooks/useFlagr";
import { useIntercomClient } from "../../../hooks/useIntercom";

interface EmploymentFormData extends FieldValues {
  effectiveDate: string;
}

interface Props<FormData extends EmploymentFormData> {
  control: Control<FormData>;
}

export default function EffectiveDateField<FormData extends EmploymentFormData>({ control }: Props<FormData>) {
  const isFutureDateEnabled = useFeatureFlags()["membership-change-form-future-date"];
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({
    control,
    name: "effectiveDate" as Path<FormData>,
    rules: { required: "You must enter a start date" },
  });

  const intercom = useIntercomClient();
  const callMembershipServices = () => openURL(`tel:800-474-2776`);

  const pastDateErrorMessage = (
    <Text className="text-ink text-small-tight font-400">
      Your start date must be within the last 4 months. If your start date is more than 4 months ago, please{" "}
      <Text className="text-primary font-400" onPress={() => intercom.presentMessageComposer()}>
        contact membership services online
      </Text>{" "}
      or call{" "}
      <Text className="text-primary font-400" onPress={callMembershipServices}>
        800-474-2776
      </Text>{" "}
      for assistance.
    </Text>
  );

  function validate(date: Moment) {
    const futureMonth = 4;
    const futureDate = moment().add(futureMonth, "months");
    const pastMonth = 4;
    const pastDate = moment().subtract(pastMonth, "months");

    if (date.isBefore(pastDate)) {
      return pastDateErrorMessage;
    }

    if (isFutureDateEnabled && date.isAfter(futureDate)) {
      return `Start date cannot be more than ${futureMonth} months from now`;
    }
    if (!isFutureDateEnabled && date.isAfter(moment())) {
      return "Start date cannot be in the future";
    }
  }

  return (
    <View className="p-6 justify-between">
      <Text className="text-regular-tight font-700 text-ink-darkest">Effective Date</Text>

      <Text className="text-small-normal font-400 text-ink-darker mt-4 mb-2">Please select the start date for employment.</Text>

      <DatePickerInput
        onChange={onChange}
        value={value}
        placeholder="Start Date"
        title="Start Date"
        description="Select the start date for employment."
        validate={validate}
        onBlur={() => {}}
        error={error}
      />
    </View>
  );
}
